import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServerImage from '../components/ServerImage';
import axios from 'axios';
import { SERVER } from '../config/config';

const Portfolio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('All');
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState(['All']);
  const [loading, setLoading] = useState(true);

  // Set active filter from URL parameter on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setActiveFilter(categoryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    // Scroll to top when component mounts or location changes
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    fetchPortfolioItems();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPortfolioItems();
  }, [activeFilter]);

  const fetchPortfolioItems = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100, // Get all items for now
        category: activeFilter !== 'All' ? activeFilter : undefined
      };
      
      const response = await axios.get(`${SERVER}/portfolio`, { params });
      setProjects(response.data.items || []);
    } catch (error) {
      console.error('Error fetching portfolio items:', error);
      // Fallback to empty array if API fails
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${SERVER}/portfolio/categories`);
      setFilters(response.data || ['All']);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Keep default filters if API fails
      setFilters(['All', 'Branding', 'Content creation', 'Website', 'Mobile Apps', 'Graphic design']);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-64 pt-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-2xl lg:text-5xl font-bold text-gray-900 mb-4">
              All projects <span className="ml-4 text-sm md:text-xl font-normal">( {projects.length} projects )</span>
            </h1>
            <p className="text-gray-600 text-sm md:text-lg max-w-4xl">
              Browse our portfolio and discover the work behind our creative vision. We create bold brands, engaging websites, and striking visuals that elevate your presence and make an impact.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-4 md:gap-8 mb-12 border-b border-gray-200">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`pb-4 text-sm md:text-lg font-medium transition-colors relative ${
                  activeFilter === filter
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {projects.map((project) => (
              <div key={project.id} className="group">
                {/* Project Image */}
                <div className="relative mb-6 overflow-hidden">
                  <ServerImage
                    src={project.cover_image}
                    alt={project.title}
                    fallback="/api/pr1.png"
                    className="w-full h-96 md:h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  
                </div>

                {/* Project Info */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold text-gray-900">{project.title}</h3>
                    {project.client && (
                      <span className="text-sm text-gray-500">{project.client} • {project.year}</span>
                    )}
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>

                  {/* Tags and View Button */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-3">
                      {project.tags && project.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="flex items-center bg-soft-gray px-2 py-1 rounded-full gap-2 text-xs md:text-sm text-gray-600"
                        >
                          <span 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: 'var(--primary-blue)' }}
                          ></span>
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div>
                      <button 
                      onClick={() => navigate(`/portfolio/${project.slug}`)}
                      className="text-white px-6 py-3 max-md:hidden rounded-full flex items-center gap-2 font-semibold hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'var(--primary-blue)' }}
                    >
                      View
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path 
                          d="M7 17L17 7M17 7H7M17 7V17" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    </div>
                    
                  </div>
                  <div className='flex justify-end'>
                    <button 
                      onClick={() => navigate(`/portfolio/${project.slug}`)}
                      className="text-white md:hidden px-4 py-2 rounded-full shadow-xl flex items-center gap-2 font-semibold hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'var(--primary-blue)' }}
                    >
                      View
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path 
                          d="M7 17L17 7M17 7H7M17 7V17" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                  </div>
                  
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {projects.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Projects Found</h3>
              <p className="text-gray-600">
                {activeFilter !== 'All' 
                  ? `No projects found in the "${activeFilter}" category.`
                  : 'Check back soon for our latest projects and case studies.'
                }
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Portfolio;